import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tab,
  Tabs
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const ADMIN_EMAILS = [process.env.REACT_APP_ADMIN_EMAIL, 'topswebusiness@gmail.com'].filter(Boolean);

const adminPageSx = {
  py: { xs: 5, md: 7 },
  minHeight: 'calc(100vh - 120px)',
  '& .MuiCard-root, & .MuiPaper-root': {
    background: (theme) => `${theme.palette.background.paper} !important`,
    backgroundImage: 'none !important',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '20px !important',
    boxShadow: (theme) =>
      theme.palette.mode === 'dark'
        ? '0 20px 70px rgba(0, 0, 0, 0.22)'
        : '0 20px 70px rgba(82, 107, 170, 0.1)',
  },
  '& .MuiTableCell-head': {
    color: 'text.secondary !important',
    fontWeight: '800 !important',
    bgcolor: 'action.hover',
  },
  '& .MuiTableCell-root': {
    borderColor: 'divider',
  },
};

function AdminInfluencers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [influencerCodes, setInfluencerCodes] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state for new influencer
  const [newInfluencer, setNewInfluencer] = useState({
    code: '',
    influencer_name: '',
    influencer_email: '',
    commission_percent: 50
  });

  // Payout reference
  const [payoutReference, setPayoutReference] = useState('');

  useEffect(() => {
    if (!user || !ADMIN_EMAILS.includes(user.email)) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load influencer codes
      const { data: codes, error: codesError } = await supabase
        .from('influencer_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (codesError) throw codesError;
      setInfluencerCodes(codes || []);

      // Load commissions with influencer info
      const { data: comms, error: commsError } = await supabase
        .from('influencer_commissions')
        .select(`
          *,
          influencer_codes (code, influencer_name)
        `)
        .order('created_at', { ascending: false });

      if (commsError) throw commsError;
      setCommissions(comms || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({ open: true, message: 'Error loading data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInfluencer = async () => {
    try {
      const { data, error } = await supabase
        .from('influencer_codes')
        .insert({
          code: newInfluencer.code.toUpperCase(),
          influencer_name: newInfluencer.influencer_name,
          influencer_email: newInfluencer.influencer_email,
          commission_percent: newInfluencer.commission_percent
        })
        .select()
        .single();

      if (error) throw error;

      setInfluencerCodes([data, ...influencerCodes]);
      setDialogOpen(false);
      setNewInfluencer({ code: '', influencer_name: '', influencer_email: '', commission_percent: 50 });
      setSnackbar({ open: true, message: 'Influencer code created!', severity: 'success' });
    } catch (error) {
      console.error('Error creating influencer:', error);
      setSnackbar({ open: true, message: error.message || 'Error creating influencer', severity: 'error' });
    }
  };

  const handleToggleActive = async (id, currentActive) => {
    try {
      const { error } = await supabase
        .from('influencer_codes')
        .update({ active: !currentActive })
        .eq('id', id);

      if (error) throw error;

      setInfluencerCodes(influencerCodes.map(ic =>
        ic.id === id ? { ...ic, active: !currentActive } : ic
      ));
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedCommission) return;

    try {
      const { error } = await supabase
        .from('influencer_commissions')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
          payout_reference: payoutReference
        })
        .eq('id', selectedCommission.id);

      if (error) throw error;

      setCommissions(commissions.map(c =>
        c.id === selectedCommission.id
          ? { ...c, status: 'paid', paid_at: new Date().toISOString(), payout_reference: payoutReference }
          : c
      ));
      setPayoutDialogOpen(false);
      setSelectedCommission(null);
      setPayoutReference('');
      setSnackbar({ open: true, message: 'Commission marked as paid', severity: 'success' });
    } catch (error) {
      console.error('Error marking paid:', error);
      setSnackbar({ open: true, message: 'Error marking paid', severity: 'error' });
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate totals
  const totalPendingCommissions = commissions
    .filter(c => c.status === 'pending')
    .reduce((sum, c) => sum + c.commission_amount_cents, 0);

  const totalPaidCommissions = commissions
    .filter(c => c.status === 'paid')
    .reduce((sum, c) => sum + c.commission_amount_cents, 0);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={adminPageSx}>
      <Chip label="Admin" size="small" sx={{ mb: 2, bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 700 }} />
      <Typography
        variant="h3"
        sx={{
          color: 'text.primary',
          fontWeight: 850,
          mb: 2
        }}
      >
        Influencer management.
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
        Manage influencer codes and track commissions
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                Active Influencers
              </Typography>
              <Typography variant="h3" sx={{ color: 'secondary.main', fontWeight: 850 }}>
                {influencerCodes.filter(ic => ic.active).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                Pending Payouts
              </Typography>
              <Typography variant="h3" sx={{ color: 'warning.main', fontWeight: 850 }}>
                {formatCurrency(totalPendingCommissions)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                Total Paid Out
              </Typography>
              <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 850 }}>
                {formatCurrency(totalPaidCommissions)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Influencer Codes" />
          <Tab label="Commissions" />
        </Tabs>
      </Box>

      {/* Influencer Codes Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Influencer
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Uses</TableCell>
                <TableCell>Revenue</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {influencerCodes.map((ic) => (
                <TableRow key={ic.id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{ic.code}</TableCell>
                  <TableCell>{ic.influencer_name}</TableCell>
                  <TableCell>{ic.influencer_email || '-'}</TableCell>
                  <TableCell>{ic.commission_percent}%</TableCell>
                  <TableCell>{ic.total_uses}</TableCell>
                  <TableCell>{formatCurrency(ic.total_revenue_cents)}</TableCell>
                  <TableCell>
                    <Chip
                      label={ic.active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: ic.active ? 'rgba(21, 144, 95, 0.2)' : 'rgba(136, 136, 136, 0.2)',
                        color: ic.active ? '#15905f' : '#888'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ic.active}
                          onChange={() => handleToggleActive(ic.id, ic.active)}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Commissions Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Influencer</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commissions.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{formatDate(c.created_at)}</TableCell>
                  <TableCell>{c.influencer_codes?.influencer_name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{c.influencer_codes?.code}</TableCell>
                  <TableCell>{formatCurrency(c.payment_amount_cents)}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#15905f' }}>
                    {formatCurrency(c.commission_amount_cents)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={c.status === 'paid' ? <CheckCircleIcon /> : <PendingIcon />}
                      label={c.status === 'paid' ? 'Paid' : 'Pending'}
                      size="small"
                      sx={{
                        bgcolor: c.status === 'paid' ? 'rgba(21, 144, 95, 0.2)' : 'rgba(183, 121, 31, 0.2)',
                        color: c.status === 'paid' ? '#15905f' : '#b7791f'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {c.status === 'pending' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedCommission(c);
                          setPayoutDialogOpen(true);
                        }}
                        sx={{
                          borderColor: '#15905f',
                          color: '#15905f',
                          '&:hover': { borderColor: '#0f744b', bgcolor: 'rgba(21, 144, 95, 0.1)' }
                        }}
                      >
                        Mark Paid
                      </Button>
                    )}
                    {c.status === 'paid' && c.payout_reference && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Ref: {c.payout_reference}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Create Influencer Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'background.paper' }}>Create Influencer Code</DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.paper' }}>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Code (e.g., JOHN50)"
              value={newInfluencer.code}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, code: e.target.value.toUpperCase() })}
              fullWidth
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
            <TextField
              label="Influencer Name"
              value={newInfluencer.influencer_name}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, influencer_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={newInfluencer.influencer_email}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, influencer_email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Commission Percent"
              type="number"
              value={newInfluencer.commission_percent}
              onChange={(e) => setNewInfluencer({ ...newInfluencer, commission_percent: parseInt(e.target.value) || 50 })}
              fullWidth
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'background.paper' }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateInfluencer}
            variant="contained"
            disabled={!newInfluencer.code || !newInfluencer.influencer_name}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'background.paper' }}>Mark Commission as Paid</DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.paper' }}>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Amount: <strong>{selectedCommission && formatCurrency(selectedCommission.commission_amount_cents)}</strong>
            </Typography>
            <TextField
              label="Payout Reference (e.g., PayPal transaction ID)"
              value={payoutReference}
              onChange={(e) => setPayoutReference(e.target.value)}
              fullWidth
              placeholder="e.g., PP-123456789"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'background.paper' }}>
          <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMarkPaid}
            variant="contained"
            sx={{ bgcolor: '#15905f', color: '#fff', '&:hover': { bgcolor: '#0f744b' } }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AdminInfluencers;
